"use client";

type MatchProgressStage = "collecting" | "matching" | "email" | "complete";

const PROGRESS_STAGE_TEXT: Record<MatchProgressStage, string> = {
  collecting: "Pripremam tvoje odgovore...",
  matching: "Pretražujem terapeute koji ti najviše odgovaraju...",
  email: "Šaljem ti personalizovane preporuke na email...",
  complete: "Preporuke su spremne.",
};

export interface MatchProgressSnapshot {
  summary: string;
  stage?: MatchProgressStage;
  message?: string;
  therapistCount?: number;
  isSummaryComplete?: boolean;
  resultsId?: string;
  resultsUrl?: string;
  updatedAt: number;
}

import {
  User,
  AlertCircle,
  Sparkles,
  Search,
  CheckCircle2,
  Mail,
  Calendar,
  Wallet,
  ClipboardList,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Card, CardContent } from "~/components/ui/card";
import { TherapistCard } from "~/components/therapist/therapist-card/therapist-card";
import { TherapistCardSkeleton } from "~/components/therapist/therapist-card/therapist-card-skeleton";
import {
  toTherapistCardDataFromTherapist,
  deriveBreakAvailability,
} from "~/components/therapist/therapist-card/from-search-result";
import {
  DailyAvailabilityProvider,
  useDailyAvailability,
} from "~/components/therapist/daily-availability-context";
import { api } from "~/trpc/react";
import { MarkdownWithComponents } from "~/components/patterns/mdx";
import type { RouterOutput } from "~/server/api/root";

type ToolState =
  | "input-streaming"
  | "input-available"
  | "output-available"
  | "output-error";

type ToolName =
  | "checkDescription"
  | "checkIfCouplesTherapyNeeded"
  | "prepareSearchQuery"
  | "findAndSaveTherapists"
  | "searchKnowledgeBase"
  | "searchPublicTherapists"
  | "readKnowledgeBaseSection"
  | "escalateToSupport"
  | "subscribeToNewsletter"
  | "getVerifiedTherapistPricingSummary"
  | "getMyTherapists"
  | "getMyAiAccessStatus"
  | "getMyUpcomingSchedule"
  | "getMyPaymentsSummary"
  | "getMyQuizResults"
  | "getMySessionsSchedule"
  | "getMyClients"
  | "getClientSnapshot"
  | "getClientConversationSummary"
  | "getClientsNeedingAttention"
  | "getTherapistEarningsSummary"
  | "getSessionPoliciesForContext";

interface TherapistMatch {
  therapistId: string;
  name: string;
  slug: string;
  whyThisTherapist: string[];
  missingAttributes?: string[];
  matchScore?: number;
}

interface CheckDescriptionInput {
  description?: string;
}

interface CheckDescriptionResult {
  isValid?: boolean;
  reason?: string;
  nextStep?: string;
}

interface CheckIfCouplesTherapyNeededInput {
  conversationHistory?: string;
}

interface CheckIfCouplesTherapyNeededResult {
  needsCouplesTherapy?: boolean;
  confidence?: "low" | "medium" | "high";
  reason?: string;
  shouldAsk?: boolean;
}

interface PrepareSearchQueryInput {
  description?: string;
  email?: string;
}

interface PrepareSearchQueryResult {
  success?: boolean;
  semanticQuery?: string;
  therapistGender?: string;
  email?: string;
  error?: string;
  nextStep?: string;
}

interface FindAndSaveTherapistsInput {
  email?: string;
  semanticQuery?: string;
  originalUserInput?: string;
  therapyType?: string;
  therapistGender?: string;
}

interface FindAndSaveTherapistsResult {
  success?: boolean;
  count?: number;
  therapists?: TherapistMatch[];
  answersSummary?: string;
  resultsId?: string;
  resultsUrl?: string;
  emailSent?: boolean;
  emailQueued?: boolean;
  message?: string;
  error?: string;
  requestId?: string;
}

export interface ToolInvocationProps {
  toolCallId: string;
  toolName: ToolName;
  state: ToolState;
  result?:
    | CheckDescriptionResult
    | CheckIfCouplesTherapyNeededResult
    | PrepareSearchQueryResult
    | FindAndSaveTherapistsResult;
  input?:
    | CheckDescriptionInput
    | CheckIfCouplesTherapyNeededInput
    | PrepareSearchQueryInput
    | FindAndSaveTherapistsInput;
  error?: string;
  progress?: MatchProgressSnapshot;
}

function TherapistCardWrapper({
  therapistId,
  index,
  isCouplesTherapy,
  whyThisTherapist,
  missingAttributes,
  isHighestMatch,
}: {
  therapistId: string;
  index: number;
  isCouplesTherapy: boolean;
  whyThisTherapist: string[];
  missingAttributes: string[];
  isHighestMatch: boolean;
}) {
  const therapist = api.therapistRouter.getTherapist.useQuery({
    therapistId,
  });

  if (therapist.isLoading) {
    return <TherapistCardSkeleton />;
  }

  if (therapist.isError || !therapist.data) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Nismo uspeli da učitamo informacije o terapeutu.
        </AlertDescription>
      </Alert>
    );
  }

  if (!therapist.data.isVerified || therapist.data.isIncognito) {
    return null;
  }

  return (
    <LoadedTherapistCard
      therapist={therapist.data}
      index={index}
      isCouplesTherapy={isCouplesTherapy}
      whyThisTherapist={whyThisTherapist}
      missingAttributes={missingAttributes}
      isHighestMatch={isHighestMatch}
    />
  );
}

function LoadedTherapistCard({
  therapist,
  index,
  isCouplesTherapy,
  whyThisTherapist,
  missingAttributes,
  isHighestMatch,
}: {
  therapist: NonNullable<RouterOutput["therapistRouter"]["getTherapist"]>;
  index: number;
  isCouplesTherapy: boolean;
  whyThisTherapist: string[];
  missingAttributes: string[];
  isHighestMatch: boolean;
}) {
  const dailyAvailability = useDailyAvailability(therapist.slug);
  const therapistCardData = toTherapistCardDataFromTherapist(
    therapist,
    therapist.sessionsHeld ?? 0,
  );
  const breakAvailability = deriveBreakAvailability(therapist);

  return (
    <div style={{ animationDelay: `${index * 50}ms` }}>
      <TherapistCard
        therapist={therapistCardData}
        isCouplesTherapy={isCouplesTherapy}
        availability={breakAvailability ?? dailyAvailability}
        match={{
          reasons: whyThisTherapist || [],
          isBest: isHighestMatch,
        }}
        missingAttributes={missingAttributes}
        renderJsonLd={false}
      />
    </div>
  );
}

function MatchingStatusLine({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="my-2 mr-auto flex max-w-[92%] items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground sm:max-w-[85%]">
      <Icon className="h-4 w-4 animate-pulse" />
      <span>{children}</span>
    </div>
  );
}

function CheckDescriptionStreaming({
  input,
}: {
  input?: CheckDescriptionInput;
}) {
  return (
    <MatchingStatusLine icon={User}>
      {input?.description
        ? "Proveravam da li imam dovoljno konteksta za preporuku..."
        : "Proveravam opis..."}
    </MatchingStatusLine>
  );
}

function CheckDescriptionComplete({}: {
  input?: CheckDescriptionInput;
  result?: CheckDescriptionResult;
}) {
  return null;
}

function PrepareSearchQueryStreaming() {
  return (
    <MatchingStatusLine icon={Sparkles}>
      Pripremam pretragu terapeuta...
    </MatchingStatusLine>
  );
}

function PrepareSearchQueryComplete({
  result,
}: {
  input?: PrepareSearchQueryInput;
  result?: PrepareSearchQueryResult;
}) {
  if (!result?.success) {
    return (
      <div className="my-2 mr-auto w-full max-w-[85%]">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Greška</AlertTitle>
          <AlertDescription>
            {result?.error || "Došlo je do greške pri pripremi pretrage."}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return null;
}

function FindAndSaveTherapistsStreaming({
  input,
  progress,
}: {
  input?: FindAndSaveTherapistsInput;
  progress?: MatchProgressSnapshot;
}) {
  const stageCopy = progress?.stage
    ? PROGRESS_STAGE_TEXT[progress.stage]
    : undefined;

  return (
    <div className="my-2 mr-auto w-full max-w-[92%] sm:max-w-[85%]">
      <div className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
        <Search className="h-4 w-4 animate-pulse" />
        <span>{stageCopy ?? "Tražim terapeute i pripremam preporuke..."}</span>
      </div>
      {stageCopy && (
        <p className="mt-2 text-xs text-muted-foreground">
          {input?.email
            ? "Preporuke će biti prikazane ovde, a email ćemo poslati kada budu spremne."
            : "Preporuke će biti prikazane ovde, bez email-a."}
        </p>
      )}
      {progress?.summary && (
        <SalesLetterPreview
          summary={progress.summary}
          isComplete={progress.isSummaryComplete}
        />
      )}
    </div>
  );
}

function FindAndSaveTherapistsError({
  result,
}: {
  result?: FindAndSaveTherapistsResult;
}) {
  return (
    <div className="my-2 mr-auto w-full max-w-[85%]">
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Greška</AlertTitle>
        <AlertDescription>
          {result?.error ||
            "Došlo je do greške pri traženju terapeuta. Molim pokušaj ponovo."}
        </AlertDescription>
      </Alert>
    </div>
  );
}

function FindAndSaveTherapistsSuccess({
  input,
  result,
  progress,
}: {
  input?: FindAndSaveTherapistsInput;
  result: FindAndSaveTherapistsResult;
  progress?: MatchProgressSnapshot;
}) {
  const { therapists = [], count = 0 } = result;

  const isCouplesTherapy = input?.therapyType === "COUPLES";
  const scoredMatches = therapists
    .map((therapist) => therapist.matchScore)
    .filter((score): score is number => typeof score === "number");
  const highestMatchScore =
    scoredMatches.length > 0 ? Math.max(...scoredMatches) : null;

  return (
    <div className="mb-4 mr-auto mt-4 w-full space-y-6">
      <div>
        <div className="mb-2 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-brand" />
          <span className="font-medium">
            Pronašli smo {count} {count === 1 ? "terapeuta" : "terapeuta"} za
            tebe
          </span>
        </div>
        <div className="space-y-4">
          {result.message && (
            <p className="text-sm text-muted-foreground">{result.message}</p>
          )}
          {result.resultsUrl && (
            <a
              href={result.resultsUrl}
              className="inline-flex min-h-9 items-center rounded-md border border-brand/25 bg-brand-action px-3 py-1.5 text-sm font-medium text-brand-foreground transition-colors hover:bg-brand-action-hover"
            >
              Pogledaj sve preporuke
            </a>
          )}
          <SalesLetterPreview
            summary={
              progress?.summary && progress.summary.trim().length > 0
                ? progress.summary
                : (result.answersSummary ?? "")
            }
            isComplete={progress?.isSummaryComplete ?? true}
          />
          <DailyAvailabilityProvider
            therapistSlugs={therapists.map((t) => t.slug)}
            sessionType={isCouplesTherapy ? "COUPLES" : "INDIVIDUAL"}
          >
            <div className="space-y-4">
              {therapists.map((therapist, index) => (
                <TherapistCardWrapper
                  key={therapist.therapistId}
                  therapistId={therapist.therapistId}
                  index={index}
                  isCouplesTherapy={isCouplesTherapy}
                  whyThisTherapist={therapist.whyThisTherapist}
                  missingAttributes={therapist.missingAttributes ?? []}
                  isHighestMatch={
                    highestMatchScore !== null &&
                    therapist.matchScore === highestMatchScore
                  }
                />
              ))}
            </div>
          </DailyAvailabilityProvider>
        </div>
      </div>
    </div>
  );
}

export function ToolInvocation({
  toolName,
  state,
  result,
  input,
  error,
  progress,
}: ToolInvocationProps) {
  if (state === "output-error") {
    return (
      <div className="my-2 mr-auto w-full max-w-[85%]">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Greška</AlertTitle>
          <AlertDescription>
            {error || "Došlo je do greške. Molim pokušaj ponovo."}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  switch (toolName) {
    case "checkDescription": {
      const checkInput = input as CheckDescriptionInput | undefined;
      const checkResult = result as CheckDescriptionResult | undefined;

      if (state === "input-streaming" || state === "input-available") {
        return <CheckDescriptionStreaming input={checkInput} />;
      }
      return (
        <CheckDescriptionComplete input={checkInput} result={checkResult} />
      );
    }

    case "checkIfCouplesTherapyNeeded": {
      return null;
    }

    case "prepareSearchQuery": {
      const prepareInput = input as PrepareSearchQueryInput | undefined;
      const prepareResult = result as PrepareSearchQueryResult | undefined;

      if (state === "input-streaming" || state === "input-available") {
        return <PrepareSearchQueryStreaming />;
      }
      return (
        <PrepareSearchQueryComplete
          input={prepareInput}
          result={prepareResult}
        />
      );
    }

    case "findAndSaveTherapists": {
      const findInput = input as FindAndSaveTherapistsInput | undefined;
      const findResult = result as FindAndSaveTherapistsResult | undefined;

      if (state === "input-streaming" || state === "input-available") {
        return (
          <FindAndSaveTherapistsStreaming
            input={findInput}
            progress={progress}
          />
        );
      }

      if (!findResult?.success) {
        return <FindAndSaveTherapistsError result={findResult} />;
      }

      return (
        <FindAndSaveTherapistsSuccess
          input={findInput}
          result={findResult}
          progress={progress}
        />
      );
    }

    case "searchKnowledgeBase": {
      if (state === "input-streaming" || state === "input-available") {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Search className="h-4 w-4 animate-pulse" />
            <span>Pretražujem podršku...</span>
          </div>
        );
      }
      return null;
    }

    case "searchPublicTherapists": {
      if (state === "input-streaming" || state === "input-available") {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Search className="h-4 w-4 animate-pulse" />
            <span>Pretražujem javne profile terapeuta...</span>
          </div>
        );
      }
      return null;
    }

    case "readKnowledgeBaseSection": {
      if (state === "input-streaming" || state === "input-available") {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Search className="h-4 w-4 animate-pulse" />
            <span>Otvaram članak...</span>
          </div>
        );
      }
      return null;
    }

    case "escalateToSupport": {
      if (state === "input-streaming" || state === "input-available") {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4 animate-pulse" />
            <span>Šaljem tvoje pitanje timu...</span>
          </div>
        );
      }

      const output =
        result && typeof result === "object"
          ? (result as {
              success?: boolean;
              message?: string;
              error?: string;
            })
          : null;

      if (output?.success === false) {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>
              {output.error ??
                "Nešto je pošlo po zlu pri prosleđivanju podršci."}
            </span>
          </div>
        );
      }

      return (
        <div className="my-2 mr-auto flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
          <CheckCircle2 className="text-brand h-4 w-4" />
          <span>
            {output?.message ??
              "Pitanje je prosledeno timu, kopija je poslata na tvoj email."}
          </span>
        </div>
      );
    }

    case "subscribeToNewsletter": {
      if (state === "input-streaming" || state === "input-available") {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4 animate-pulse" />
            <span>Prijavljujem te na povremene email-ove...</span>
          </div>
        );
      }

      const output =
        result && typeof result === "object"
          ? (result as {
              success?: boolean;
              error?: string;
            })
          : null;

      if (output?.success === false) {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            <span>Prijava trenutno nije uspela. Možeš pokušati kasnije.</span>
          </div>
        );
      }

      return (
        <div className="my-2 mr-auto flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
          <CheckCircle2 className="text-brand h-4 w-4" />
          <span>Prijavljen/a si na popuste, ponude i savete na email.</span>
        </div>
      );
    }

    case "getMyTherapists": {
      if (state === "input-streaming" || state === "input-available") {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4 animate-pulse" />
            <span>Proveravam tvoje terapeute...</span>
          </div>
        );
      }
      return null;
    }

    case "getMyAiAccessStatus": {
      if (state === "input-streaming" || state === "input-available") {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Wallet className="h-4 w-4 animate-pulse" />
            <span>Proveravam tvoj AI paket...</span>
          </div>
        );
      }

      const output =
        result && typeof result === "object"
          ? (result as {
              success?: boolean;
              summary?: string;
              deletionEffect?: string;
              error?: string;
            })
          : null;

      if (output?.success === false) {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>
              {output.error ?? "Ne mogu trenutno da proverim AI pristup."}
            </span>
          </div>
        );
      }

      return (
        <div className="my-2 mr-auto rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="text-brand h-4 w-4" />
            <span>{output?.summary ?? "Proverio sam status AI pristupa."}</span>
          </div>
          {output?.deletionEffect ? (
            <p className="mt-1 text-xs leading-5">{output.deletionEffect}</p>
          ) : null}
        </div>
      );
    }

    case "getMyUpcomingSchedule": {
      if (state === "input-streaming" || state === "input-available") {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4 animate-pulse" />
            <span>Proveravam tvoj raspored...</span>
          </div>
        );
      }
      return null;
    }

    case "getVerifiedTherapistPricingSummary":
    case "getMyPaymentsSummary": {
      if (state === "input-streaming" || state === "input-available") {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Wallet className="h-4 w-4 animate-pulse" />
            <span>
              {toolName === "getVerifiedTherapistPricingSummary"
                ? "Proveravam aktuelne cene i trajanje..."
                : "Proveravam stanje uplata i kredita..."}
            </span>
          </div>
        );
      }
      return null;
    }

    case "getMyQuizResults": {
      if (state === "input-streaming" || state === "input-available") {
        return (
          <div className="my-2 mr-auto flex items-center gap-2 text-sm text-muted-foreground">
            <ClipboardList className="h-4 w-4 animate-pulse" />
            <span>Proveravam tvoje testove...</span>
          </div>
        );
      }
      return null;
    }

    default:
      return null;
  }
}

export const TherapistMatchingTool = ToolInvocation;

function SalesLetterPreview({
  summary,
  isComplete,
}: {
  summary?: string;
  isComplete?: boolean;
}) {
  if (!summary || summary.trim().length === 0) {
    return null;
  }

  return (
    <div className="mt-4 rounded-lg border border-dashed border-border/60 bg-muted/20 p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Preporuka {isComplete ? "(spremno)" : "(pišem...)"}
      </p>
      <div className="prose prose-sm max-w-none text-foreground">
        <MarkdownWithComponents>{summary}</MarkdownWithComponents>
      </div>
      {!isComplete && (
        <p className="mt-2 text-xs text-muted-foreground">
          Još uvek pišem preporuku...
        </p>
      )}
    </div>
  );
}
